// src/redux/companyProspect/companyProspectSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createCompanyProspectAPI,
  getCompanyProspectGeneratedUrlAPI,
  getCompanyProspectListingAPI,
  getCompanyProspectDetailAPI,
  updateCompanyProspectAPI,
  generateCompanyProspectUrlAPI,
  deleteCompanyProspectAPI,
  getCompanyEmployeesAPI,
  getCompanyEmployeeDetailAPI,
  convertCompanyProspectAPI,
} from "./companyProspectApi";

// ✅ Create Company Prospect
export const createCompanyProspect = createAsyncThunk(
  "companyProspect/createCompanyProspect",
  async (prospectData, { rejectWithValue }) => {
    try {
      const response = await createCompanyProspectAPI(prospectData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create company prospect"
      );
    }
  }
);

// ✅ Get Company Prospect Generated URL
export const getCompanyProspectGeneratedUrl = createAsyncThunk(
  "companyProspect/getCompanyProspectGeneratedUrl",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await getCompanyProspectGeneratedUrlAPI(companyId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch generated URL"
      );
    }
  }
);

// ✅ Generate Company Prospect URL
export const generateCompanyProspectUrl = createAsyncThunk(
  "companyProspect/generateCompanyProspectUrl",
  async (prospectData, { rejectWithValue }) => {
    try {
      const response = await generateCompanyProspectUrlAPI(prospectData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to generate company prospect URL"
      );
    }
  }
);

// ✅ Get Company Prospect Listing
export const getCompanyProspectListing = createAsyncThunk(
  "companyProspect/getCompanyProspectListing",
  async ({ page = 1, limit = 10, search = "" } = {}, { rejectWithValue }) => {
    try {
      const response = await getCompanyProspectListingAPI({
        page,
        limit,
        search,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch company prospect listing"
      );
    }
  }
);

// ✅ Get Company Prospect Detail
export const getCompanyProspectDetail = createAsyncThunk(
  "companyProspect/getCompanyProspectDetail",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await getCompanyProspectDetailAPI(companyId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch company prospect detail"
      );
    }
  }
);

// ✅ Delete Company Prospect
export const deleteCompanyProspect = createAsyncThunk(
  "companyProspect/deleteCompanyProspect",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await deleteCompanyProspectAPI(companyId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete company prospect"
      );
    }
  }
);

// ✅ Update Company Prospect
export const updateCompanyProspect = createAsyncThunk(
  "companyProspect/updateCompanyProspect",
  async ({ companyId, prospectData }, { rejectWithValue }) => {
    try {
      const response = await updateCompanyProspectAPI(companyId, prospectData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update company prospect"
      );
    }
  }
);

// ✅ Get Company Employees
export const getCompanyEmployees = createAsyncThunk(
  "companyProspect/getCompanyEmployees",
  async (
    { companyId, page = 1, limit = 10, search = "" } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await getCompanyEmployeesAPI({
        companyId,
        page,
        limit,
        search,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch company employees"
      );
    }
  }
);

// ✅ Get Company Employee Detail
export const getCompanyEmployeeDetail = createAsyncThunk(
  "companyProspect/getCompanyEmployeeDetail",
  async ({ companyId, employeeId }, { rejectWithValue }) => {
    try {
      const response = await getCompanyEmployeeDetailAPI(companyId, employeeId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch employee detail"
      );
    }
  }
);

// ✅ Convert Company Prospect
export const convertCompanyProspect = createAsyncThunk(
  "companyProspect/convertCompanyProspect",
  async ({ companyId, prospectData }, { rejectWithValue }) => {
    try {
      const response = await convertCompanyProspectAPI(companyId, prospectData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to convert company prospect"
      );
    }
  }
);

// ---------- Initial State ----------
const initialState = {
  createCompanyProspect: { data: null, loading: false, error: null },
  getCompanyProspectGeneratedUrl: { data: null, loading: false, error: null },
  generateCompanyProspectUrl: { data: null, loading: false, error: null },
  getCompanyProspectListing: { data: [], total: 0, loading: false, error: null },
  getCompanyProspectDetail: { data: null, loading: false, error: null },
  updateCompanyProspect: { data: null, loading: false, error: null },
  deleteCompanyProspect: { loading: false, error: null },
  getCompanyEmployees: { data: [], total: 0, loading: false, error: null },
  getCompanyEmployeeDetail: { data: null, loading: false, error: null },
  convertCompanyProspect: { data: null, loading: false, error: null },
};

// ---------- Slice ----------
const companyProspectSlice = createSlice({
  name: "companyProspect",
  initialState,
  reducers: {
    clearCreateCompanyProspect: (state) => {
      state.createCompanyProspect.data = null;
      state.createCompanyProspect.error = null;
    },
    clearCompanyProspectGeneratedUrl: (state) => {
      state.getCompanyProspectGeneratedUrl.data = null;
      state.getCompanyProspectGeneratedUrl.error = null;
    },
    clearGenerateCompanyProspectUrl: (state) => {
      state.generateCompanyProspectUrl.data = null;
      state.generateCompanyProspectUrl.error = null;
    },
    clearCompanyProspectListing: (state) => {
      state.getCompanyProspectListing.data = [];
      state.getCompanyProspectListing.total = 0;
      state.getCompanyProspectListing.error = null;
    },
    clearCompanyProspectDetail: (state) => {
      state.getCompanyProspectDetail.data = null;
      state.getCompanyProspectDetail.error = null;
    },
    clearCompanyEmployees: (state) => {
      state.getCompanyEmployees.data = [];
      state.getCompanyEmployees.total = 0;
      state.getCompanyEmployees.error = null;
    },
    clearCompanyEmployeeDetail: (state) => {
      state.getCompanyEmployeeDetail.data = null;
      state.getCompanyEmployeeDetail.error = null;
    },
    clearConvertCompanyProspect: (state) => {
      state.convertCompanyProspect.data = null;
      state.convertCompanyProspect.error = null;
    },
  },
  extraReducers: (builder) => {
    // ---------- Create Company Prospect ----------
    builder
      .addCase(createCompanyProspect.pending, (state) => {
        state.createCompanyProspect.loading = true;
        state.createCompanyProspect.error = null;
      })
      .addCase(createCompanyProspect.fulfilled, (state, action) => {
        state.createCompanyProspect.loading = false;
        state.createCompanyProspect.data =
          action.payload?.data || action.payload;
      })
      .addCase(createCompanyProspect.rejected, (state, action) => {
        state.createCompanyProspect.loading = false;
        state.createCompanyProspect.error = action.payload;
      });

    // ---------- Get Company Prospect Generated URL ----------
    builder
      .addCase(getCompanyProspectGeneratedUrl.pending, (state) => {
        state.getCompanyProspectGeneratedUrl.loading = true;
        state.getCompanyProspectGeneratedUrl.error = null;
      })
      .addCase(getCompanyProspectGeneratedUrl.fulfilled, (state, action) => {
        state.getCompanyProspectGeneratedUrl.loading = false;
        state.getCompanyProspectGeneratedUrl.data =
          action.payload?.data || action.payload;
      })
      .addCase(getCompanyProspectGeneratedUrl.rejected, (state, action) => {
        state.getCompanyProspectGeneratedUrl.loading = false;
        state.getCompanyProspectGeneratedUrl.error = action.payload;
      });

    // ---------- Generate Company Prospect URL ----------
    builder
      .addCase(generateCompanyProspectUrl.pending, (state) => {
        state.generateCompanyProspectUrl.loading = true;
        state.generateCompanyProspectUrl.error = null;
      })
      .addCase(generateCompanyProspectUrl.fulfilled, (state, action) => {
        state.generateCompanyProspectUrl.loading = false;
        state.generateCompanyProspectUrl.data =
          action.payload?.data || action.payload;
      })
      .addCase(generateCompanyProspectUrl.rejected, (state, action) => {
        state.generateCompanyProspectUrl.loading = false;
        state.generateCompanyProspectUrl.error = action.payload;
      });

    // ---------- Get Company Prospect Listing ----------
    builder
      .addCase(getCompanyProspectListing.pending, (state) => {
        state.getCompanyProspectListing.loading = true;
        state.getCompanyProspectListing.error = null;
      })
      .addCase(getCompanyProspectListing.fulfilled, (state, action) => {
        state.getCompanyProspectListing.loading = false;
        state.getCompanyProspectListing.data = action.payload?.data || [];
        state.getCompanyProspectListing.total =
          action.payload?.total || action.payload?.pagination?.total || 0;
      })
      .addCase(getCompanyProspectListing.rejected, (state, action) => {
        state.getCompanyProspectListing.loading = false;
        state.getCompanyProspectListing.error = action.payload;
      });

    // ---------- Get Company Prospect Detail ----------
    builder
      .addCase(getCompanyProspectDetail.pending, (state) => {
        state.getCompanyProspectDetail.loading = true;
        state.getCompanyProspectDetail.error = null;
      })
      .addCase(getCompanyProspectDetail.fulfilled, (state, action) => {
        state.getCompanyProspectDetail.loading = false;
        state.getCompanyProspectDetail.data =
          action.payload?.data || action.payload;
      })
      .addCase(getCompanyProspectDetail.rejected, (state, action) => {
        state.getCompanyProspectDetail.loading = false;
        state.getCompanyProspectDetail.error = action.payload;
      });

    // ---------- Delete Company Prospect ----------
    builder
      .addCase(deleteCompanyProspect.pending, (state) => {
        state.deleteCompanyProspect.loading = true;
        state.deleteCompanyProspect.error = null;
      })
      .addCase(deleteCompanyProspect.fulfilled, (state) => {
        state.deleteCompanyProspect.loading = false;
      })
      .addCase(deleteCompanyProspect.rejected, (state, action) => {
        state.deleteCompanyProspect.loading = false;
        state.deleteCompanyProspect.error = action.payload;
      });

    // ---------- Update Company Prospect ----------
    builder
      .addCase(updateCompanyProspect.pending, (state) => {
        state.updateCompanyProspect.loading = true;
        state.updateCompanyProspect.error = null;
      })
      .addCase(updateCompanyProspect.fulfilled, (state, action) => {
        state.updateCompanyProspect.loading = false;
        state.updateCompanyProspect.data =
          action.payload?.data || action.payload;
      })
      .addCase(updateCompanyProspect.rejected, (state, action) => {
        state.updateCompanyProspect.loading = false;
        state.updateCompanyProspect.error = action.payload;
      });

    // ---------- Get Company Employees ----------
    builder
      .addCase(getCompanyEmployees.pending, (state) => {
        state.getCompanyEmployees.loading = true;
        state.getCompanyEmployees.error = null;
      })
      .addCase(getCompanyEmployees.fulfilled, (state, action) => {
        state.getCompanyEmployees.loading = false;
        state.getCompanyEmployees.data = action.payload?.data || [];
        state.getCompanyEmployees.total =
          action.payload?.total || action.payload?.pagination?.total || 0;
      })
      .addCase(getCompanyEmployees.rejected, (state, action) => {
        state.getCompanyEmployees.loading = false;
        state.getCompanyEmployees.error = action.payload;
      });

    // ---------- Get Company Employee Detail ----------
    builder
      .addCase(getCompanyEmployeeDetail.pending, (state) => {
        state.getCompanyEmployeeDetail.loading = true;
        state.getCompanyEmployeeDetail.error = null;
      })
      .addCase(getCompanyEmployeeDetail.fulfilled, (state, action) => {
        state.getCompanyEmployeeDetail.loading = false;
        state.getCompanyEmployeeDetail.data =
          action.payload?.data || action.payload;
      })
      .addCase(getCompanyEmployeeDetail.rejected, (state, action) => {
        state.getCompanyEmployeeDetail.loading = false;
        state.getCompanyEmployeeDetail.error = action.payload;
      });

    // ---------- Convert Company Prospect ----------
    builder
      .addCase(convertCompanyProspect.pending, (state) => {
        state.convertCompanyProspect.loading = true;
        state.convertCompanyProspect.error = null;
      })
      .addCase(convertCompanyProspect.fulfilled, (state, action) => {
        state.convertCompanyProspect.loading = false;
        state.convertCompanyProspect.data =
          action.payload?.data || action.payload;
      })
      .addCase(convertCompanyProspect.rejected, (state, action) => {
        state.convertCompanyProspect.loading = false;
        state.convertCompanyProspect.error = action.payload;
      });
  },
});

export const {
  clearCreateCompanyProspect,
  clearCompanyProspectGeneratedUrl,
  clearGenerateCompanyProspectUrl,
  clearCompanyProspectListing,
  clearCompanyProspectDetail,
  clearCompanyEmployees,
  clearCompanyEmployeeDetail,
  clearConvertCompanyProspect,
} = companyProspectSlice.actions;

export default companyProspectSlice.reducer;